/**
 * Matches ASCII control chars (`\x00`-`\x1F`, `\x7F`) and zero-width chars.
 * Path-significant chars (`/`, `-`, case) are intentionally left untouched —
 * Replicate / Fal model names are case-sensitive path strings.
 */
// eslint-disable-next-line no-control-regex -- intentionally matches ASCII control chars
export const CONTROL_AND_INVISIBLE_CHARS = /[\x00-\x1F\x7F\u200B-\u200D\uFEFF]/g;

// Percent-encoded ASCII control chars (`%00`-`%1F`, `%7F`) \u2014 e.g. `%0D%0A` for a pasted CRLF.
// These reach us as ordinary text, so the raw-control-char strip above misses them. Matched
// explicitly (rather than blanket-decoding) so legitimate escapes like `%2F` are left intact.
const PERCENT_ENCODED_CONTROL_CHARS = /%(?:[01][0-9a-f]|7f)/gi;

// A backslash is never valid in a Replicate / Fal `owner/model` path; it's a Windows-path or
// pasted/escaped-URL artifact (e.g. `bytedance\seedance-2.0-fast`). Canonicalize to the forward-slash
// form the price index is keyed by so lookups match.
export const normalizeModelName = (name: string): string =>
  name.replace(PERCENT_ENCODED_CONTROL_CHARS, '').replace(CONTROL_AND_INVISIBLE_CHARS, '').replace(/\\/g, '/').trim();

// Canonicalize an `owner/name` model id for price-index lookups. Beyond normalizeModelName (which
// only trims the whole string's ends), this trims whitespace around each `/` separator and drops
// empty segments left by stray/duplicate/leading/trailing separators (e.g. a `\`-turned-`/` next to
// a real `/`, or `/owner/name/`). So however a user dirties the name, it still resolves against the
// clean, forward-slash-keyed index. Replicate/Fal ids never contain empty segments, so this only
// ever changes dirty input — legit multi-segment names like `fal-ai/flux/dev` pass through.
export const canonicalizeModelId = (name: string): string =>
  normalizeModelName(name)
    .split('/')
    .map((segment) => segment.trim())
    .filter((segment) => segment.length > 0)
    .join('/');
